<template>
  <!-- [matrah] Meta message-template builder: one template name, up to 5 languages. -->
  <v-container fluid class="create-template pa-4 pa-md-6">
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" :to="{ name: 'templates', query: { section: 'library' } }">
        Templates
      </v-btn>
      <div>
        <div class="text-h5 font-weight-bold">Create template</div>
        <div class="text-body-2 text-medium-emphasis">
          Submitted to Meta for review, one version per language. Approval usually takes minutes to a few hours.
        </div>
      </div>
      <v-spacer />
      <v-btn color="primary" variant="flat" size="large" prepend-icon="mdi-send-check" :loading="submitting"
        :disabled="!canSubmit" @click="submit">
        Submit for review
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12" lg="8">
        <!-- Identity -->
        <v-card variant="flat" class="mb-4 panel">
          <v-card-title class="panel-title">
            <v-icon icon="mdi-tag-outline" size="18" class="mr-1" /> Template identity
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="nameInput" @update:model-value="onNameInput" label="Template name"
                  variant="outlined" density="comfortable" prepend-inner-icon="mdi-identifier"
                  :rules="[rules.nameRequired, rules.nameFormat]" placeholder="e.g. Orphan payment reminder"
                  :hint="name ? 'Meta name: ' + name : 'Lowercase letters, numbers and underscores only'" persistent-hint />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="category" :items="categories" item-title="title" item-value="value" label="Category"
                  variant="outlined" density="comfortable" prepend-inner-icon="mdi-shape-outline"
                  :hint="categoryHint" persistent-hint>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :subtitle="item.raw.hint" />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-select v-model="selectedCodes" :items="allLanguages" item-title="name" item-value="code"
                  label="Languages" variant="outlined" density="comfortable" multiple chips closable-chips
                  prepend-inner-icon="mdi-translate" :rules="[rules.languagesRequired]" hint="Each selected language gets its own tab below" persistent-hint>
                  <template v-slot:chip="{ props, item }">
                    <v-chip v-bind="props" size="small">
                      <LanguageFlag :code="item.raw.code" :size="16" class="mr-1" /> {{ item.raw.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :subtitle="item.raw.native">
                      <template v-slot:prepend>
                        <LanguageFlag :code="item.raw.code" :size="20" class="mr-3" />
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Per-language content -->
        <v-card variant="flat" class="panel">
          <v-tabs v-model="activeCode" color="primary" bg-color="surface-light" density="comfortable" show-arrows>
            <v-tab v-for="lang in languages" :key="lang.code" :value="lang.code">
              <LanguageFlag :code="lang.code" :size="18" class="mr-2" />
              {{ languageName(lang.code) }}
              <v-icon v-if="languageErrors(lang).length" icon="mdi-alert-circle" color="error" size="16" class="ml-1" />
              <v-icon v-else-if="lang.body" icon="mdi-check-circle" color="success" size="16" class="ml-1" />
            </v-tab>
          </v-tabs>
          <v-divider />
          <v-card-text v-if="!languages.length" class="text-medium-emphasis text-center py-8">
            Select at least one language above to start writing the message.
          </v-card-text>
          <v-window v-else v-model="activeCode">
            <v-window-item v-for="(lang, idx) in languages" :key="lang.code" :value="lang.code">
              <v-card-text>
                <TemplateLanguageEditor :model-value="lang" @update:model-value="setLanguage(idx, $event)"
                  :copy-sources="copySourcesFor(lang.code)" :errors="languageErrors(lang)"
                  @copy-from="copyFrom($event, lang.code)" />
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>

      <v-col cols="12" lg="4">
        <div class="preview-sticky">
          <div class="text-subtitle-2 text-medium-emphasis mb-2 d-flex align-center">
            <v-icon icon="mdi-cellphone" size="18" class="mr-1" /> Live preview
            <v-spacer />
            <span v-if="activeLanguage">{{ languageName(activeLanguage.code) }}</span>
          </div>
          <WhatsAppPreview v-if="activeLanguage" :language="activeLanguage" />
          <v-sheet v-else class="pa-6 text-center text-medium-emphasis rounded-lg" color="surface-light">
            Preview appears once a language is selected.
          </v-sheet>

          <v-card variant="tonal" color="primary" class="mt-4">
            <v-card-text class="text-body-2">
              <div class="font-weight-medium mb-1">Meta review tips</div>
              <ul class="pl-4">
                <li>Body cannot start or end with a variable.</li>
                <li v-text="variableOrderTip"></li>
                <li>Quick replies and call-to-action buttons are sent grouped.</li>
                <li>Utility templates must be transactional; promotional text is rejected.</li>
              </ul>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>

    <!-- Results -->
    <v-dialog v-model="resultDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon :icon="result.success ? 'mdi-check-decagram' : 'mdi-alert-circle'"
            :color="result.success ? 'success' : 'error'" class="mr-2" />
          {{ result.success ? 'Submitted to Meta' : 'Submission failed' }}
        </v-card-title>
        <v-card-text>
          <p v-if="result.message" class="text-body-2 mb-3">{{ result.message }}</p>

          <v-list v-if="result.results && result.results.length" density="compact" class="result-list">
            <v-list-item v-for="(r, i) in result.results" :key="i">
              <template v-slot:prepend>
                <LanguageFlag :code="r.language" :size="20" class="mr-3" />
              </template>
              <v-list-item-title>{{ languageName(r.language) }} <span class="text-medium-emphasis">({{ r.language }})</span></v-list-item-title>
              <v-list-item-subtitle v-if="r.error" class="text-error">{{ describeError(r.error) }}</v-list-item-subtitle>
              <v-list-item-subtitle v-else-if="r.id">Meta id: {{ r.id }}</v-list-item-subtitle>
              <template v-slot:append>
                <v-chip size="small" :color="statusColor(r.status || (r.error ? 'REJECTED' : 'PENDING'))" variant="flat">
                  {{ r.status || (r.error ? 'ERROR' : 'PENDING') }}
                </v-chip>
              </template>
            </v-list-item>
          </v-list>

          <div v-if="validationErrors.length">
            <div class="text-subtitle-2 mb-1">Validation errors</div>
            <ul class="pl-4 text-body-2 text-error">
              <li v-for="(e, i) in validationErrors" :key="i">{{ e }}</li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resultDialog = false">Keep editing</v-btn>
          <v-btn v-if="result.success" color="primary" variant="flat"
            :to="{ name: 'templates', query: { section: 'library' } }">
            Go to templates
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'pinia'
import { useTemplateStore } from '../stores/template'
import { useGlobalStore } from '../stores/global'
import LanguageFlag from '@/components/templates/LanguageFlag.vue'
import TemplateLanguageEditor from '@/components/templates/TemplateLanguageEditor.vue'
import WhatsAppPreview from '@/components/templates/WhatsAppPreview.vue'
import {
  META_LIMITS,
  TEMPLATE_CATEGORIES,
  TEMPLATE_LANGUAGES,
  extractVariables,
  languageMeta,
  slugifyTemplateName,
  statusColor,
} from '@/utils/templateLanguages'

function emptyLanguage(code) {
  return {
    code,
    header: { type: 'NONE', text: '', example_url: '' },
    body: '',
    body_examples: [],
    footer: '',
    buttons: [],
  }
}

function isHttpsUrl(value) {
  try {
    const u = new URL(value)
    return u.protocol === 'https:' || u.protocol === 'http:'
  } catch (e) {
    return false
  }
}

export default {
  name: 'TemplateCreateView',
  components: { LanguageFlag, TemplateLanguageEditor, WhatsAppPreview },
  data: () => ({
    nameInput: '',
    name: '',
    category: 'UTILITY',
    categories: TEMPLATE_CATEGORIES,
    allLanguages: TEMPLATE_LANGUAGES,
    selectedCodes: ['ar', 'en'],
    languages: [emptyLanguage('ar'), emptyLanguage('en')],
    activeCode: 'ar',
    submitting: false,
    resultDialog: false,
    result: { success: false, results: [], message: '' },
    validationErrors: [],
    variableOrderTip: 'Variables must be numbered in order: {{1}}, {{2}} …',
    rules: {
      nameRequired: (v) => !!slugifyTemplateName(v) || 'Name is required',
      nameFormat: (v) => /^[a-z0-9_]*$/.test(slugifyTemplateName(v)) || 'Only lowercase letters, numbers and underscores',
      languagesRequired: (v) => (Array.isArray(v) && v.length > 0) || 'Pick at least one language',
    },
  }),
  computed: {
    categoryHint() {
      return this.categories.find((c) => c.value === this.category)?.hint || ''
    },
    activeLanguage() {
      return this.languages.find((l) => l.code === this.activeCode) || this.languages[0] || null
    },
    canSubmit() {
      return (
        !!this.name &&
        !!this.category &&
        this.languages.length > 0 &&
        this.languages.every((l) => this.languageErrors(l).length === 0)
      )
    },
  },
  watch: {
    selectedCodes(codes) {
      // keep existing content for languages that stay selected; order follows TEMPLATE_LANGUAGES
      const ordered = TEMPLATE_LANGUAGES.map((l) => l.code).filter((c) => codes.includes(c))
      this.languages = ordered.map((code) => this.languages.find((l) => l.code === code) || emptyLanguage(code))
      if (!ordered.includes(this.activeCode)) this.activeCode = ordered[0] || null
    },
  },
  mounted() {
    document.title = 'Create template'
  },
  methods: {
    ...mapActions(useTemplateStore, ['createTemplate']),
    ...mapActions(useGlobalStore, ['setAlertMessage']),
    statusColor,
    languageName(code) {
      return languageMeta(code).name
    },
    onNameInput(value) {
      this.name = slugifyTemplateName(value)
    },
    setLanguage(idx, value) {
      this.languages.splice(idx, 1, value)
    },
    copySourcesFor(code) {
      return this.languages
        .filter((l) => l.code !== code && (l.body || l.buttons.length || l.header.type !== 'NONE'))
        .map((l) => ({ code: l.code, name: this.languageName(l.code) }))
    },
    copyFrom(sourceCode, targetCode) {
      const source = this.languages.find((l) => l.code === sourceCode)
      const idx = this.languages.findIndex((l) => l.code === targetCode)
      if (!source || idx < 0) return
      const clone = JSON.parse(JSON.stringify(source))
      clone.code = targetCode
      this.languages.splice(idx, 1, clone)
      this.setAlertMessage({
        display: true,
        text: `Copied ${this.languageName(sourceCode)} content into ${this.languageName(targetCode)} — translate it before submitting.`,
        class: 'info',
      })
    },
    languageErrors(lang) {
      const errors = []
      const L = META_LIMITS
      const body = (lang.body || '').trim()
      if (!body) errors.push('Body is required.')
      if (body.length > L.bodyMax) errors.push(`Body exceeds ${L.bodyMax} characters.`)
      const vars = extractVariables(body)
      const max = vars.length ? Math.max(...vars) : 0
      for (let n = 1; n <= max; n++) {
        if (!vars.includes(n)) errors.push(`Variables must be sequential: {{${n}}} is missing.`)
      }
      if (max > 0) {
        if (/^\{\{\s*\d+\s*\}\}/.test(body)) errors.push('Body cannot start with a variable.')
        if (/\{\{\s*\d+\s*\}\}$/.test(body)) errors.push('Body cannot end with a variable.')
        for (let n = 1; n <= max; n++) {
          const sample = (lang.body_examples || [])[n - 1]
          if (!sample || !String(sample).trim()) errors.push(`Sample value for {{${n}}} is required.`)
        }
      }
      const h = lang.header || { type: 'NONE' }
      if (h.type === 'TEXT') {
        if (!h.text || !h.text.trim()) errors.push('Header text is required when header type is Text.')
        else if (h.text.length > L.headerTextMax) errors.push(`Header exceeds ${L.headerTextMax} characters.`)
        if (/\{\{/.test(h.text || '')) errors.push('Header text cannot contain variables.')
      }
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(h.type)) {
        if (!h.example_url || !isHttpsUrl(h.example_url)) errors.push('A valid example URL is required for a media header.')
      }
      if ((lang.footer || '').length > L.footerMax) errors.push(`Footer exceeds ${L.footerMax} characters.`)

      const counts = { QUICK_REPLY: 0, URL: 0, PHONE_NUMBER: 0 }
      const texts = new Set()
      ;(lang.buttons || []).forEach((b, i) => {
        counts[b.type] = (counts[b.type] || 0) + 1
        const label = `Button ${i + 1}`
        if (!b.text || !b.text.trim()) errors.push(`${label}: text is required.`)
        else if (b.text.length > L.buttonTextMax) errors.push(`${label}: text exceeds ${L.buttonTextMax} characters.`)
        if (b.text && texts.has(b.text.trim().toLowerCase())) errors.push(`${label}: duplicate button text.`)
        if (b.text) texts.add(b.text.trim().toLowerCase())
        if (b.type === 'URL') {
          if (!b.url || !isHttpsUrl(b.url)) errors.push(`${label}: a valid URL is required.`)
          if (b.dynamic && (!b.example || !isHttpsUrl(b.example))) errors.push(`${label}: example URL is required for a dynamic suffix.`)
        }
        if (b.type === 'PHONE_NUMBER') {
          if (!b.phone_number || !/^\+?[0-9 ]{6,20}$/.test(b.phone_number)) errors.push(`${label}: phone number must be in international format.`)
        }
      })
      if ((lang.buttons || []).length > L.buttonsMax) errors.push(`Maximum ${L.buttonsMax} buttons.`)
      if (counts.QUICK_REPLY > L.quickReplyMax) errors.push(`Maximum ${L.quickReplyMax} quick-reply buttons.`)
      if (counts.URL > L.urlMax_count) errors.push(`Maximum ${L.urlMax_count} URL buttons.`)
      if (counts.PHONE_NUMBER > L.phoneMax_count) errors.push(`Maximum ${L.phoneMax_count} phone button.`)
      return errors
    },
    buildPayload() {
      return {
        name: this.name,
        category: this.category,
        languages: this.languages.map((lang) => {
          const header = { type: lang.header.type }
          if (header.type === 'TEXT') header.text = lang.header.text.trim()
          if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type)) header.example_url = lang.header.example_url.trim()

          const vars = extractVariables(lang.body)
          const max = vars.length ? Math.max(...vars) : 0
          const body_examples = Array.from({ length: max }, (_, i) => String((lang.body_examples || [])[i] || '').trim())

          // Meta requires quick replies and call-to-action buttons to be grouped.
          const quick = lang.buttons.filter((b) => b.type === 'QUICK_REPLY')
          const cta = lang.buttons.filter((b) => b.type !== 'QUICK_REPLY')
          const buttons = [...quick, ...cta].map((b) => {
            const out = { type: b.type, text: b.text.trim() }
            if (b.type === 'URL') {
              const base = b.url.trim()
              out.url = b.dynamic ? `${base}{{1}}` : base
              if (b.dynamic) out.example = b.example.trim()
            }
            if (b.type === 'PHONE_NUMBER') out.phone_number = b.phone_number.replace(/\s+/g, '')
            return out
          })

          const item = { code: lang.code, header, body: lang.body.trim(), body_examples, buttons }
          if (lang.footer && lang.footer.trim()) item.footer = lang.footer.trim()
          return item
        }),
      }
    },
    describeError(error) {
      if (!error) return ''
      if (typeof error === 'string') return error
      return error.error_user_msg || error.message || error.error?.message || JSON.stringify(error)
    },
    flattenErrors(errors) {
      if (!errors) return []
      if (Array.isArray(errors)) return errors.map((e) => (typeof e === 'string' ? e : e.msg || e.message || JSON.stringify(e)))
      if (typeof errors === 'object') {
        return Object.keys(errors).map((k) => {
          const v = errors[k]
          return `${k}: ${Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v : JSON.stringify(v)}`
        })
      }
      return [String(errors)]
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.validationErrors = []
      try {
        const res = await this.createTemplate(this.buildPayload())
        this.result = res
        this.validationErrors = this.flattenErrors(res.errors)
        this.resultDialog = true
        if (res.success) {
          this.setAlertMessage({ display: true, text: `Template "${this.name}" submitted to Meta.`, class: 'success' })
        } else {
          // [matrah] error toast as well (the result dialog keeps the per-language details)
          const firstError = this.validationErrors[0]
          this.setAlertMessage({
            display: true,
            text: `Template "${this.name}" was not created: ${res.message || firstError || 'Meta rejected the request'}`,
            class: 'error',
            timeout: 7000,
          })
        }
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.create-template {
  max-width: 1500px;
}

.panel {
  border: 1px solid #dfe6e0;
  border-radius: 12px;
  background: #fff;
}

.panel-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  padding: 12px 16px 0;
}

.preview-sticky {
  position: sticky;
  top: 80px;
}

.result-list {
  background: transparent;
}
</style>
